import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import AuthenticationService from "../../services/AuthenticationService";
import { validateVerificationCodeInput } from "../../utils/verificationCodeErrorHandler";
import "./LoginTwoFactorModal.css";

const CODE_LENGTH = 6;

const LoginTwoFactorModal = ({ isOpen, onClose, method, email, redirectUrl, password, onSuccess }) => {
  const { t } = useTranslation();
  const [codeValues, setCodeValues] = useState(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [requestingRecovery, setRequestingRecovery] = useState(false);

  const inputRefs = useRef([]);

  const isAppMethod = method === "app" && !isRecoveryMode;
  const code = codeValues.join("");

  const TEXT = {
    title_app: t("auth.twoFactorTitle", "Two-Factor Authentication"),
    title_email: t("auth.emailVerification", "Email Verification"),
    label_app_code: t("auth.twoFactorAppLabel", "Enter the 6-digit code from your authenticator app"),
    label_email_code: t("auth.twoFactorEmailLabel", "Enter the verification code from your email"),
    hint_email: (e) => t("auth.hintEmail", `We sent a verification code to ${e}.`, { email: e }),
    hint_recovery_email: (e) => t("auth.hintRecoveryEmail", `We sent a recovery code to ${e}.`, { email: e }),
    cancel: t("auth.cancel", "Cancel"),
    verify: t("auth.verify", "Verify"),
    verifying: t("auth.verifying", "Verifying..."),
    error_code_invalid: t("auth.errorInvalidCode", "Enter a valid verification code."),
    error_invalid_2fa_code: t("auth.errorInvalid2faCode", "Invalid verification code. Please try again."),
    error_generic: t("common.errorGeneric", "Something went wrong. Please try again."),
    no_access_to_authenticator: t("auth.noAccessAuthenticator", "Don't have access to your authenticator app?"),
    sending_recovery_code: t("auth.sendingRecoveryCode", "Sending recovery code..."),
    error_recovery_unavailable: t("auth.errorRecoveryUnavailable", "Recovery code request unavailable."),
    error_recovery_request: t("auth.errorRecoveryRequest", "Failed to send recovery code. Please try again."),
    recovery_method_changed_notice: t("auth.recoveryMethodNotice", "Your 2FA method will be changed to email after verification."),
    digit_label: (index) => t("auth.digitLabel", "Digit {{index}}", { index }),
  };

  const resetState = () => {
    setCodeValues(Array(CODE_LENGTH).fill(""));
    setError("");
    setSubmitting(false);
    setIsRecoveryMode(false);
    setRequestingRecovery(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen, method, email]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 0);
    }
  }, [isOpen, method]);

  const handleClose = () => {
    if (submitting) return;
    resetState();
    onClose();
  };

  const sanitizeChar = (value) => {
    if (isAppMethod) {
      return value.replace(/[^0-9]/g, "");
    }

    return value.replace(/[^a-zA-Z0-9]/g, "");
  };

  const validateTwoFactorCode = () => {
    if (isAppMethod) {
      return validateVerificationCodeInput(code);
    }

    if (code.length !== CODE_LENGTH) {
      return TEXT.error_code_invalid;
    }

    if (!/^[a-zA-Z0-9]{6}$/.test(code)) {
      return TEXT.error_code_invalid;
    }

    return null;
  };

  const parseTwoFactorError = (response) => {
    const detail = response?.data?.detail;

    switch (detail) {
      case "Invalid 2FA code":
        return TEXT.error_invalid_2fa_code;
      default:
        return detail || TEXT.error_generic;
    }
  };

  const handleCodeChange = (index, rawValue) => {
    const sanitized = sanitizeChar(rawValue);

    if (!sanitized) {
      const next = [...codeValues];
      next[index] = "";
      setCodeValues(next);
      setError("");
      return;
    }

    if (sanitized.length > 1) {
      const chars = sanitized.slice(0, CODE_LENGTH).split("");
      const next = Array(CODE_LENGTH).fill("");

      chars.forEach((char, idx) => {
        next[idx] = char;
      });

      setCodeValues(next);
      setError("");

      const focusIndex = Math.min(chars.length, CODE_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    const next = [...codeValues];
    next[index] = sanitized;
    setCodeValues(next);
    setError("");

    if (index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (codeValues[index]) {
        const next = [...codeValues];
        next[index] = "";
        setCodeValues(next);
        setError("");
        return;
      }

      if (index > 0) {
        inputRefs.current[index - 1]?.focus();

        const next = [...codeValues];
        next[index - 1] = "";
        setCodeValues(next);
        setError("");
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();

    const pasted = sanitizeChar(e.clipboardData.getData("text")).slice(0, CODE_LENGTH);

    if (!pasted) return;

    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((char, idx) => {
      next[idx] = char;
    });

    setCodeValues(next);
    setError("");

    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleRequestRecovery = async () => {
    if (!email) {
      setError(TEXT.error_recovery_unavailable);
      return;
    }

    try {
      setRequestingRecovery(true);
      setError("");

      await AuthenticationService.requestTwoFactorRecovery(email);

      setCodeValues(Array(CODE_LENGTH).fill(""));
      setIsRecoveryMode(true);

      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 0);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || TEXT.error_recovery_request);
    } finally {
      setRequestingRecovery(false);
    }
  };

  const handleSubmit = async () => {
    setError("");

    const validationError = validateTwoFactorCode();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);

      const normalizedCode = code.trim();

      const response = isAppMethod
        ? await AuthenticationService.verifyLoginApp(email, normalizedCode, redirectUrl)
        : await AuthenticationService.verifyLoginEmail(email, normalizedCode, redirectUrl);

      resetState();
      onSuccess(response, email, password);
    } catch (err) {
      const friendly = parseTwoFactorError(err.response);
      setError(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isAppMethod ? TEXT.title_app : TEXT.title_email}
      actions={
        <>
          <SecondButton onClick={handleClose} disabled={submitting}>
            {TEXT.cancel}
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={submitting}>
            {submitting ? TEXT.verifying : TEXT.verify}
          </MainButton>
        </>
      }
    >
      <div className="two-factor-modal-form">
        <label className="two-factor-modal-label">{isAppMethod ? TEXT.label_app_code : TEXT.label_email_code}</label>

        <div className="two-factor-otp-group" onPaste={handleCodePaste}>
          {codeValues.map((char, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              className="two-factor-otp-input"
              value={char}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleCodeKeyDown(index, e)}
              onFocus={(e) => e.target.select()}
              inputMode={isAppMethod ? "numeric" : "text"}
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              aria-label={TEXT.digit_label(index + 1)}
              disabled={submitting}
              style={{ textTransform: "none" }}
            />
          ))}
        </div>

        {!isAppMethod && (
          <p className="two-factor-modal-hint">
            {isRecoveryMode ? TEXT.hint_recovery_email(email) : TEXT.hint_email(email)}
          </p>
        )}

        {method === "app" && !isRecoveryMode && (
          <button
            type="button"
            className="two-factor-recovery-link"
            onClick={handleRequestRecovery}
            disabled={submitting || requestingRecovery}
          >
            {requestingRecovery ? TEXT.sending_recovery_code : TEXT.no_access_to_authenticator}
          </button>
        )}

        {isRecoveryMode && <p className="two-factor-recovery-notice">{TEXT.recovery_method_changed_notice}</p>}
        {error && (
          <div className="two-factor-modal-error">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LoginTwoFactorModal;
